/**
 * Lightweight <img> post-processor.
 *
 * 1. Gives any image that is *missing* an `alt` attribute a German fallback.
 *    Images with an intentional empty `alt=""` (decorative) are left alone.
 * 2. Repoints any image whose source fails to load at a static placeholder
 *    (`/assets/placeholder.svg`).
 */

const ALT_FALLBACK = 'Unbenanntes Bild';
const PLACEHOLDER_SRC = '/assets/placeholder.svg';

function ensureAlt(img: HTMLImageElement): void {
  if (!img.hasAttribute('alt')) {
    img.setAttribute('alt', ALT_FALLBACK);
  }
}

function replaceWithPlaceholder(img: HTMLImageElement): void {
  // The error event can fire more than once (src changes, retries, and the
  // placeholder itself failing) — only swap once to avoid an infinite loop.
  if (img.dataset.imgFallback) return;
  img.dataset.imgFallback = 'done';

  // `_image.twig` renders <picture class="contents"> with a <source srcset>
  // (webp) and an <img> that also carries srcset/sizes. The browser resolves
  // the displayed image from those candidates, so overwriting `src` alone is
  // ignored — strip every srcset/source first, then point `src` at the
  // placeholder.
  img
    .closest('picture')
    ?.querySelectorAll('source')
    .forEach((source) => source.remove());
  img.removeAttribute('srcset');
  img.removeAttribute('sizes');
  img.src = PLACEHOLDER_SRC;
}

function handleError(event: Event): void {
  const target = event.target;
  if (target instanceof HTMLImageElement) {
    replaceWithPlaceholder(target);
  }
}

function processExistingImages(): void {
  document.querySelectorAll('img').forEach((img) => {
    ensureAlt(img);

    const src = img.getAttribute('src');
    if (
      img.complete &&
      img.naturalWidth === 0 &&
      src &&
      !/\.svg($|\?)/i.test(src)
    ) {
      replaceWithPlaceholder(img);
    }
  });
}

export function initImageFallback(): void {
  document.addEventListener('error', handleError, true);

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', processExistingImages);
  } else {
    processExistingImages();
  }
}
